import { api } from './api';
import type { PaginatedResponse } from '../types';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

/**
 * The nine supported datatypes.
 *
 * `ENCRYPTED` is the odd one: its stored value is an inline AES-GCM envelope, so
 * the API never returns it in the clear to a non-administrator — a masked
 * stand-in comes back instead. It is written by sending `rawValue`; sending
 * `value` for one is rejected, because the server owns the ciphertext.
 */
export type AppConfigDataType =
  | 'STRING'
  | 'INTEGER'
  | 'DECIMAL'
  | 'BOOLEAN'
  | 'ARRAY'
  | 'OBJECT'
  | 'URL'
  | 'VALUESET_REF'
  | 'ENCRYPTED';

export const APP_CONFIG_DATA_TYPES: AppConfigDataType[] = [
  'STRING', 'INTEGER', 'DECIMAL', 'BOOLEAN', 'ARRAY',
  'OBJECT', 'URL', 'VALUESET_REF', 'ENCRYPTED',
];

/** What the API substitutes for an ENCRYPTED value the caller may not read. */
export const MASKED_SECRET = '***encrypted***';

export interface AppConfig {
  _id: string;
  configKey: string;
  /** Owning project. `null` is this deployment's DEFAULT row — every project
   *  without its own falls back to it, and only a platform administrator may
   *  edit one. */
  appId: string | null;
  description: string;
  dataType: AppConfigDataType;
  value: unknown;
  defaultValue: unknown;
  allowedValues: unknown[] | null;
  isActive: boolean;
  /** Where the row came from, and the only thing deciding whether it may be
   *  deleted. `PLATFORM` rows are seeded or written at project provisioning —
   *  application code reads them by name, so the API refuses to delete one.
   *  Editing is unaffected: value, description and defaults stay open. */
  origin?: 'PLATFORM' | 'OPERATOR';
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}


export interface AppConfigHistoryEntry {
  _id: string;
  configId: string;
  configKey: string;
  appId: string | null;
  action: 'CREATE' | 'UPDATE';
  changedFields: string[];
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changedBy?: string | null;
  changedAt?: string | null;
}

export interface AppConfigCreateRequest {
  configKey: string;
  description: string;
  dataType: AppConfigDataType;
  value?: unknown;
  defaultValue?: unknown;
  allowedValues?: unknown[] | null;
  isActive?: boolean;
  appId?: string | null;
  /** ENCRYPTED only, write-only. The plaintext credential; the server encrypts
   *  it and never echoes it back. */
  rawValue?: string;
}

/** `configKey`, `dataType` and `appId` are absent by design — all three are
 *  immutable, and re-scoping is its own endpoint. `isActive` is absent too:
 *  retiring a configuration is a separate act under APP_CONFIG.DELETE. */
export interface AppConfigUpdateRequest {
  description?: string;
  value?: unknown;
  defaultValue?: unknown;
  allowedValues?: unknown[] | null;
  rawValue?: string;
}

export interface AppConfigQueryParams {
  search?: string;
  dataType?: AppConfigDataType;
  isActive?: boolean;
  appId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  /** Serialised per-column filters, as the shared data table produces them. */
  filters?: string;
}

export const appConfigService = {
  getAll: async (params?: AppConfigQueryParams): Promise<PaginatedResponse<AppConfig>> => {
    const response = await api.get<PaginatedResponse<AppConfig>>('/api/v1/app-configs/list', { params });
    return response.data;
  },

  getByKey: async (configKey: string, appId?: string): Promise<AppConfig> => {
    const response = await api.get<ApiResponse<AppConfig>>(
      `/api/v1/app-configs/get/${encodeURIComponent(configKey)}`, { params: { appId } });
    return response.data.data;
  },

  create: async (payload: AppConfigCreateRequest): Promise<AppConfig> => {
    const response = await api.post<ApiResponse<AppConfig>>('/api/v1/app-configs/create', payload);
    return response.data.data;
  },

  update: async (configKey: string, payload: AppConfigUpdateRequest, appId?: string): Promise<AppConfig> => {
    const response = await api.put<ApiResponse<AppConfig>>(
      `/api/v1/app-configs/update/${encodeURIComponent(configKey)}`, payload, { params: { appId } });
    return response.data.data;
  },

  /** Deactivate a configuration. Nothing is deleted — the row and its history stay,
   *  so a key application code still references resolves to a controlled error
   *  rather than vanishing. */
  deactivate: async (configKey: string, appId?: string): Promise<AppConfig> => {
    const response = await api.patch<ApiResponse<AppConfig>>(
      `/api/v1/app-configs/deactivate/${encodeURIComponent(configKey)}`, undefined, { params: { appId } });
    return response.data.data;
  },

  activate: async (configKey: string, appId?: string): Promise<AppConfig> => {
    const response = await api.patch<ApiResponse<AppConfig>>(
      `/api/v1/app-configs/activate/${encodeURIComponent(configKey)}`, undefined, { params: { appId } });
    return response.data.data;
  },

  /** Soft-delete a configuration. It drops out of the list, but the record and
   *  its history are kept (and it stops being read at runtime), so it can be
   *  restored and a key still referenced resolves to a controlled error. */
  delete: async (configKey: string, appId?: string): Promise<AppConfig> => {
    const response = await api.delete<ApiResponse<AppConfig>>(
      `/api/v1/app-configs/delete/${encodeURIComponent(configKey)}`, { params: { appId } });
    return response.data.data;
  },

  getHistory: async (
    configKey: string,
    params?: { appId?: string; page?: number; pageSize?: number },
  ): Promise<PaginatedResponse<AppConfigHistoryEntry>> => {
    const response = await api.get<PaginatedResponse<AppConfigHistoryEntry>>(
      `/api/v1/app-configs/history/${encodeURIComponent(configKey)}`, { params });
    return response.data;
  },

  /** Move a configuration to another project. Platform administrators only. */
  rescope: async (configId: string, appId: string | null): Promise<AppConfig> => {
    const response = await api.patch<ApiResponse<AppConfig>>(
      `/api/v1/app-configs/${configId}/project`, { appId });
    return response.data.data;
  },
};

// ---------------------------------------------------------------------------
// Background-job tunables
// ---------------------------------------------------------------------------

/** The block field names the settings screens use, mapped to their config keys. */
export const BACKGROUND_JOB_KEYS = {
  engineEnabled: 'BackgroundJobs.EngineEnabled',
  maxConcurrency: 'BackgroundJobs.MaxConcurrency',
  lockTtlSeconds: 'BackgroundJobs.LockTtlSeconds',
  runRetentionDays: 'BackgroundJobs.RunRetentionDays',
  pollIntervalSeconds: 'BackgroundJobs.PollIntervalSeconds',
  tickToken: 'BackgroundJobs.TickToken',
} as const;

const JOB_KEY_TYPES: Record<string, AppConfigDataType> = {
  [BACKGROUND_JOB_KEYS.engineEnabled]: 'BOOLEAN',
  [BACKGROUND_JOB_KEYS.maxConcurrency]: 'INTEGER',
  [BACKGROUND_JOB_KEYS.lockTtlSeconds]: 'INTEGER',
  [BACKGROUND_JOB_KEYS.runRetentionDays]: 'INTEGER',
  [BACKGROUND_JOB_KEYS.pollIntervalSeconds]: 'INTEGER',
};

const JOB_KEY_DESCRIPTIONS: Record<string, string> = {
  [BACKGROUND_JOB_KEYS.engineEnabled]: "Whether this project's background jobs run at all.",
  [BACKGROUND_JOB_KEYS.maxConcurrency]: 'Most jobs this project may run at once.',
  [BACKGROUND_JOB_KEYS.lockTtlSeconds]: 'How long a claimed job holds its lock before it may be re-claimed.',
  [BACKGROUND_JOB_KEYS.runRetentionDays]: 'Days of run history to keep. 0 keeps it forever.',
  [BACKGROUND_JOB_KEYS.pollIntervalSeconds]: "How often this project's schedules are examined.",
  [BACKGROUND_JOB_KEYS.tickToken]: "Token authenticating this project's /tick heartbeat.",
};

/**
 * Create the configuration if this project has none, otherwise update it.
 *
 * `update` alone is not enough: a project inherits until it writes its own row,
 * so the first save for any field is a create. Falling back on 404 rather than
 * reading first keeps it to one round trip in the common case.
 */
const upsert = async (
  configKey: string,
  projectId: string,
  body: { value?: unknown; rawValue?: string },
): Promise<void> => {
  try {
    await appConfigService.update(configKey, body, projectId);
  } catch (error) {
    // Only a genuine "no row for this project yet" falls through to a create.
    // Anything else — a 403 on a Default row, a 400 on a bad key — must surface
    // as itself rather than be retried as a create that will fail differently.
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status !== 404) throw error;
    await appConfigService.create({
      configKey,
      description: JOB_KEY_DESCRIPTIONS[configKey] ?? 'Project configuration.',
      dataType: body.rawValue !== undefined ? 'ENCRYPTED' : (JOB_KEY_TYPES[configKey] ?? 'STRING'),
      appId: projectId,
      ...body,
    });
  }
};

/** A project's background-job overrides, in the block shape the screens render.
 *
 *  Read straight from AppConfiguration rather than from the project-settings
 *  endpoint, which no longer carries the block at all. One source, so the screen
 *  that displays these values and the screen that edits them cannot disagree.
 *
 *  `hasTickToken` reports presence only; the credential itself is never returned.
 */
export const getBackgroundJobSettings = async (
  projectId: string,
): Promise<Record<string, unknown> & { hasTickToken: boolean }> => {
  const wanted = Object.values(BACKGROUND_JOB_KEYS);
  const rows = await appConfigService.getAll({
    appId: projectId,
    pageSize: 100,
    // Inactive rows are excluded: a deactivated setting is not an override, and
    // showing one as active is how an operator comes to trust a value nothing reads.
    isActive: true,
  });

  const byKey = new Map((rows.data || []).map((r) => [r.configKey, r]));
  const block: Record<string, unknown> = {};
  for (const [field, configKey] of Object.entries(BACKGROUND_JOB_KEYS)) {
    if (field === 'tickToken' || !wanted.includes(configKey)) continue;
    const row = byKey.get(configKey);
    // Null means "inherits the deployment default" and must stay absent — a
    // present key reads as an override everywhere downstream.
    if (row && row.value !== null && row.value !== undefined) block[field] = row.value;
  }
  return { ...block, hasTickToken: byKey.has(BACKGROUND_JOB_KEYS.tickToken) };
};

/**
 * Save a project's background-job overrides as individual configuration rows.
 *
 * `null` is sent EXPLICITLY for a blank field, and that is the mechanism rather
 * than an omission: clearing the value is how a project stops overriding a
 * setting and returns to the deployment default. Leaving the key out instead
 * would keep the last value it had, so a field an operator emptied would quietly
 * stay in force.
 *
 * The tick token is the exception — it is omitted when blank. Sending null there
 * would encrypt an empty credential and revoke a working token every time an
 * operator saved an unrelated field.
 */
export const saveBackgroundJobSettings = async (
  projectId: string,
  values: {
    engineEnabled: boolean | null;
    maxConcurrency: number | null;
    lockTtlSeconds: number | null;
    runRetentionDays: number | null;
    pollIntervalSeconds: number | null;
    rawTickToken?: string;
  },
): Promise<void> => {
  const { rawTickToken, ...tunables } = values;

  for (const [field, value] of Object.entries(tunables)) {
    const configKey = BACKGROUND_JOB_KEYS[field as keyof typeof BACKGROUND_JOB_KEYS];
    if (!configKey) continue;
    await upsert(configKey, projectId, { value });
  }

  if (rawTickToken && rawTickToken.trim()) {
    await upsert(BACKGROUND_JOB_KEYS.tickToken, projectId, { rawValue: rawTickToken.trim() });
  }
};

// ---------------------------------------------------------------------------
// Value rendering / parsing
// ---------------------------------------------------------------------------

/** A one-line summary for the list column. Never reveals a credential. */
export const summariseValue = (config: Pick<AppConfig, 'dataType' | 'value'>): string => {
  const { dataType, value } = config;
  if (dataType === 'ENCRYPTED') return '••••••••';
  if (value === null || value === undefined) return '—';
  if (dataType === 'BOOLEAN') return value ? 'true' : 'false';
  if (dataType === 'ARRAY' || dataType === 'OBJECT') {
    const text = JSON.stringify(value);
    return text.length > 60 ? `${text.slice(0, 60)}…` : text;
  }
  return String(value);
};

/** Parse what the form's editor produced back into the datatype's shape.
 *
 *  Returns `{ ok: false, error }` rather than throwing, so the form can show the
 *  message beside the field. The backend validates this again — this is for the
 *  operator's benefit, not the server's. */
export const parseValueForType = (
  dataType: AppConfigDataType,
  raw: string,
): { ok: true; value: unknown } | { ok: false; error: string } => {
  const text = (raw ?? '').trim();
  if (!text) return { ok: true, value: null };

  switch (dataType) {
    case 'INTEGER': {
      const n = Number(text);
      return Number.isInteger(n)
        ? { ok: true, value: n }
        : { ok: false, error: 'Must be a whole number.' };
    }
    case 'DECIMAL': {
      const n = Number(text);
      return Number.isFinite(n)
        ? { ok: true, value: n }
        : { ok: false, error: 'Must be a number.' };
    }
    case 'BOOLEAN':
      return { ok: true, value: text.toLowerCase() === 'true' };
    case 'ARRAY':
    case 'OBJECT': {
      try {
        const parsed = JSON.parse(text);
        const wanted = dataType === 'ARRAY' ? Array.isArray(parsed) : (
          typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed));
        return wanted
          ? { ok: true, value: parsed }
          : { ok: false, error: `Must be a JSON ${dataType === 'ARRAY' ? 'array' : 'object'}.` };
      } catch {
        return { ok: false, error: 'Must be valid JSON.' };
      }
    }
    case 'URL':
      return /^https?:\/\/.+/i.test(text)
        ? { ok: true, value: text }
        : { ok: false, error: 'Must be an http(s) URL.' };
    default:
      return { ok: true, value: text };
  }
};

/** Render a stored value into the editor's text form. */
export const stringifyValue = (dataType: AppConfigDataType, value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (dataType === 'ARRAY' || dataType === 'OBJECT') return JSON.stringify(value, null, 2);
  return String(value);
};

// ---------------------------------------------------------------------------
// Integration Config fields
// ---------------------------------------------------------------------------

/** One property of `Integration.<Channel>.<Provider>.Config`, as the API describes it. */
export interface IntegrationConfigField {
  name: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'array';
  hint: string | null;
  placeholder: string | null;
  options: { label: string; value: string }[] | null;
  advanced: boolean;
  /** A send needs this one. */
  required: boolean;
  /** Required only in a given mode, e.g. the OAuth client id while authType is
   *  oauth2. `null` when it is unconditional (or not required at all). */
  requiredWhen: { field: string; equals: unknown } | null;
}

/** Which described fields are required for the values as they stand, conditionals
 *  included. Exported so the editor and the form's save check agree. */
export const missingRequired = (
  fields: IntegrationConfigField[], value: Record<string, unknown>,
): IntegrationConfigField[] => fields.filter((field) => {
  const needed = field.required
    || (!!field.requiredWhen && value?.[field.requiredWhen.field] === field.requiredWhen.equals);
  if (!needed) return false;
  const current = value?.[field.name];
  return current === null || current === undefined || current === ''
    || (Array.isArray(current) && current.length === 0);
});

export interface IntegrationFieldSpec {
  channel: string;
  provider: string | null;
  fields: IntegrationConfigField[];
  /** Where this channel's credentials live — never inside `Config`. */
  secretKeys: { field: string; key: string }[];
  /** Extra things the screen can do for this channel, e.g. 'test', 'oauth'. */
  actions: string[];
  /** What the deployment already uses, so an editor opens on a complete object
   *  rather than a sparse overlay. The row being edited wins, property by
   *  property, so nothing appears twice. */
  startingValues: Record<string, unknown>;
}

/** Describes the Config object a channel reads. An undescribed channel returns no
 *  fields, and the screen falls back to the JSON editor. */
export const getIntegrationFields = async (
  channel: string, provider?: string,
): Promise<IntegrationFieldSpec> => {
  const response = await api.get<ApiResponse<IntegrationFieldSpec>>(
    '/api/v1/app-configs/integration-fields', { params: { channel, provider } });
  return response.data.data;
};
